import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, IconButton, Typography } from '@altermaind/web/ui';
// eslint-disable-next-line @altermaind/no-build-import
import hardSkillAnswerGuide from '@/_shared/assets/documents/Hard_skill_assessment_Spiegazione_risposte.pdf';
import { Charts } from '@/_shared/components/Charts';
import { ClaimsComponent } from '@/_shared/components/Claims';
import { ReadMoreTypography } from '@/_shared/components/ReadMoreTypography';
import { Roles } from '@/_shared/constants/roles';
import { downloadFile } from '@/_shared/helpers/download';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getEmployeesMeCall } from '@/profile/state/api';
import { getTemplatesCall, postEmployerByIdSurveyResultCall } from '@/surveys/state';
import { SurveyTemplate } from '@/surveys/state/types';
import { ResultsNotVisible } from '../ResultsNotVisible';
import { SurveyResultsVsDepartmentAndTeam } from '../SurveyResultsVsDepartmentAndTeam';
import { SurveyResultsVsOrganization } from '../SurveyResultsVsOrganization';
import { SurveyResultsVsTeam } from '../SurveyResultsVsTeam';

type Props = {
  templateId?: string;
};

export const AssessmentResults = ({ templateId }: Props) => {
  const t = useTranslations('assessments.surveyResult');
  const { hasManagerLeader, hasTeamLeader } = getEmployeesMeCall.hook();
  const { genericResponsiveValue } = useResponsiveStyle();

  const [activeChip, setActiveChip] = useState<string>('');

  const { response: responsePostEmployerByIdSurveyResult } =
    postEmployerByIdSurveyResultCall.hook();

  const filteredResults = useMemo(() => {
    if (!responsePostEmployerByIdSurveyResult) return [];
    if (templateId) {
      return responsePostEmployerByIdSurveyResult.filter(
        survey => survey.templateId === templateId && survey.completed === true,
      );
    }
    return responsePostEmployerByIdSurveyResult.filter(survey => survey.completed === true);
  }, [responsePostEmployerByIdSurveyResult, templateId]);

  const { response: responseGetTemplates } = getTemplatesCall.hook();

  const hasMoreThanOneTemplate = useMemo(() => {
    if (templateId) return false;
    return (
      new Set(
        responsePostEmployerByIdSurveyResult
          ?.filter(survey => survey.completed === true)
          .map(survey => survey.templateId) ?? [],
      ).size > 1
    );
  }, [responsePostEmployerByIdSurveyResult, templateId]);

  const surveysToShow = templateId
    ? filteredResults
    : filteredResults.filter(survey => survey.templateName === activeChip);

  const getDescriptionMetric = useCallback(
    (surveyTemplateId: string, metricName: string) => {
      if (!responseGetTemplates) {
        return '';
      }
      return (
        responseGetTemplates
          ?.find(template => template.id === surveyTemplateId)
          ?.metrics?.find(metric => metric.name === metricName)?.description ?? ''
      );
    },
    [responseGetTemplates],
  );

  useEffect(() => {
    if (templateId && responsePostEmployerByIdSurveyResult) {
      const template = responsePostEmployerByIdSurveyResult.find(s => s.templateId === templateId);
      if (template) {
        setActiveChip(template.templateName || '');
      }
    } else if (!hasMoreThanOneTemplate && responsePostEmployerByIdSurveyResult) {
      setActiveChip(responsePostEmployerByIdSurveyResult[0]?.templateName || '');
    }
  }, [hasMoreThanOneTemplate, responsePostEmployerByIdSurveyResult, templateId]);

  return (
    <Box>
      {surveysToShow.map(survey =>
        survey.canViewResults === false ? (
          <ResultsNotVisible
            surveyTemplateName={survey.templateName}
            key={`${survey.templateId}${survey.templateVersion}`}
          />
        ) : (
          <>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              mb='xs'
              flex='2'
              flexDirection={genericResponsiveValue({
                breakPoints: { mobile: 'column' },
                defaultValue: 'row',
              })}
            >
              <Typography variant='h3'>{t('title')}</Typography>
              {survey.templateName === SurveyTemplate.HardSkillAssessment ? (
                <IconButton
                  prefixSvg='download'
                  svgStyle={{ fill: 'primary/text', size: 'xs' }}
                  textStyle={{ ml: 'xxxs' }}
                  onClick={() =>
                    downloadFile(hardSkillAnswerGuide, 'Hard Skill assessment Spiegazione risposte')
                  }
                  variant='link/primary'
                >
                  {t('downloadAnswerGuide')}
                </IconButton>
              ) : null}
            </Box>

            <Box
              flexDirection={genericResponsiveValue({
                breakPoints: { desktop: 'row' },
                defaultValue: 'column',
              })}
              key={`${survey.templateId}${survey.templateVersion}`}
              display='flex'
            >
              <Box
                mb={genericResponsiveValue({
                  breakPoints: { desktop: 'none' },
                  defaultValue: 'l',
                })}
                flex='2'
              >
                <Typography
                  variant='p3/light'
                  mr={genericResponsiveValue({
                    breakPoints: { desktop: 's' },
                    defaultValue: 'none',
                  })}
                  mb={genericResponsiveValue({
                    breakPoints: { desktop: 'l' },
                    defaultValue: 'm',
                  })}
                >
                  {t('description', {
                    metricsCount:
                      (survey &&
                        survey.results &&
                        survey.results.results &&
                        survey.results?.results.length) ??
                      0,
                    surveyTemplateName: survey.templateName,
                  })}
                </Typography>
                <Charts.RadarChartComponent
                  data={(survey.results?.results ?? []).map(metric => {
                    return {
                      subject: metric.metric?.name ?? '',
                      current: metric.score ?? 0,
                      expected: 7,
                      description: getDescriptionMetric(
                        survey.templateId || '',
                        metric.metric?.name || '',
                      ),
                    };
                  })}
                />
              </Box>
              <Box flex='1'>
                <Card>
                  <Typography variant='h4'>{t('insightBox.title')}</Typography>
                  <Box display='flex' flexDirection='column' mb='m' mt='xs'>
                    <ReadMoreTypography variant='p3/light'>
                      {Object.keys(survey.results?.insights ?? {}).map(key => (
                        <Typography variant='p3/label' key={key}>
                          {key} -{' '}
                          <Typography as='span' variant='p3/light'>
                            {(survey.results?.insights as Record<string, string>)[key]}
                          </Typography>
                          {'\n'}
                        </Typography>
                      ))}
                    </ReadMoreTypography>
                  </Box>
                  <Typography variant='h4'>{t('insightBox.proTitle')}</Typography>
                  <Box display='flex' flexDirection='column' mb='m' mt='xs'>
                    {survey.results?.results?.map(item => {
                      const maxScore = Math.max(
                        ...(survey.results?.results ?? []).map(m => m.score ?? 0),
                      );
                      if (item.score === maxScore) {
                        return (
                          <Card
                            mb='xxs'
                            backgroundColor='primary/inactive'
                            key={item.metric?.name}
                            px='xs'
                            py='xxxs'
                            width='fit-content'
                          >
                            <Typography variant='p2/light' fontSize='xs'>
                              {item.metric?.name}
                            </Typography>
                          </Card>
                        );
                      }
                      return null;
                    })}
                  </Box>
                  <Typography variant='h4'>{t('insightBox.conTitle')}</Typography>
                  <Box display='flex' flexDirection='column' mt='xs'>
                    {survey.results?.results?.map(item => {
                      const minScore = Math.min(
                        ...(survey.results?.results ?? []).map(m => m.score ?? 0),
                      );
                      if (item.score === minScore) {
                        return (
                          <Card
                            mb='xxs'
                            backgroundColor='error/opacity'
                            key={item.metric?.name}
                            px='xs'
                            py='xxxs'
                            width='fit-content'
                          >
                            <Typography variant='p2/light' fontSize='xs'>
                              {item.metric?.name}
                            </Typography>
                          </Card>
                        );
                      }
                      return null;
                    })}
                  </Box>
                </Card>
              </Box>
            </Box>
            <ClaimsComponent roles={[Roles.Employee]}>
              <SurveyResultsVsTeam surveyTemplateId={templateId} />
            </ClaimsComponent>
            <ClaimsComponent roles={[Roles.Manager]}>
              <>
                {hasTeamLeader ? (
                  <SurveyResultsVsDepartmentAndTeam surveyTemplateId={templateId} />
                ) : null}
                {hasManagerLeader ? (
                  <SurveyResultsVsOrganization surveyTemplateId={templateId} />
                ) : null}
              </>
            </ClaimsComponent>
          </>
        ),
      )}
    </Box>
  );
};
